import type { MessageNode, TimelineDisplayItem } from "./utils";
import { getTimelineDisplayItemKey, groupTimelineDisplayItems } from "./utils";
import { TimelineStep, type TimelineStepContext } from "./TimelineStep";
import { TimelineProseMessage } from "./TimelineProseMessage";
import { TimelineWorkGroupStep } from "./TimelineWorkGroupStep";

interface TimelineStepListProps {
  nodes: MessageNode[];
  ctx: TimelineStepContext;
}

/**
 * Renders a run of timeline nodes as rows. Agent messages render as flat
 * prose with no rail; the rail line and status dots run only along the steps
 * between them, so each stretch of tool work reads as its own segment.
 * Consecutive technical steps fold into one nested group, and sub-agent
 * children recurse through this same component, so nested timelines fold too.
 */
export function TimelineStepList({ nodes, ctx }: TimelineStepListProps) {
  const items = groupTimelineDisplayItems(nodes);

  return (
    <>
      {items.map((item, index) => {
        const key = getTimelineDisplayItemKey(item);

        if (item.type === "message") {
          return (
            <TimelineProseMessage
              key={key}
              node={item.node}
              isThreadActive={ctx.isThreadActive}
            />
          );
        }

        // Rail segments break at prose messages, so first/last are relative
        // to the surrounding stretch of rail steps, not the whole list, while
        // isNewest marks this list's freshest row for the green dot.
        const isFirst = isRailSegmentBoundary(items[index - 1]);
        const isLast = isRailSegmentBoundary(items[index + 1]);
        const isNewest = index === items.length - 1;

        if (item.type === "workGroup") {
          return (
            <TimelineWorkGroupStep
              key={key}
              nodes={item.nodes}
              ctx={ctx}
              isFirst={isFirst}
              isLast={isLast}
              isNewest={isNewest}
            />
          );
        }

        return (
          <TimelineStep
            key={key}
            node={item.node}
            ctx={ctx}
            rail={{ isFirst, isLast, isNewest }}
          />
        );
      })}
    </>
  );
}

/** Whether a neighboring item ends a rail segment: a prose message or the list edge. */
function isRailSegmentBoundary(item: TimelineDisplayItem | undefined): boolean {
  return item === undefined || item.type === "message";
}
